/**
 * Auth Telemetry Service
 *
 * Captures authentication events (login success/failure) for observability.
 * Makes C1 (Adoption Backplan: Reliability and observability) true.
 *
 * Evidence: audit_events table contains LoginSucceeded/LoginFailed rows
 */

import { getSupabaseClient } from './supabaseClient';

export type AuthEventType =
  | 'LoginSucceeded'
  | 'LoginFailed'
  | 'LogoutCompleted'
  | 'SessionExpired'
  | 'PermissionDenied';

export interface AuthEventPayload {
  type: AuthEventType;
  timestamp: number;
  userId?: string;
  email?: string;
  tenantId?: string;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
}

export async function recordAuthEvent(
  event: AuthEventPayload,
  createdBy: string
): Promise<void> {
  if (import.meta.env.DEV) {
    console.debug('[AuthTelemetry]', event.type, { userId: createdBy, ...event });
  }

  try {
    const supabase = getSupabaseClient();

    const { error } = await supabase
      .from('audit_events')
      .insert({
        event_type: event.type,
        payload: event,
        created_by: createdBy,
      });

    if (error && import.meta.env.DEV) {
      console.warn('[AuthTelemetry] Failed to record event:', error);
    }
  } catch (err) {
    // Silent failure — telemetry must not block user flows
    if (import.meta.env.DEV) {
      console.warn('[AuthTelemetry] Error recording event:', err);
    }
  }
}

export async function recordLoginSuccess(
  userId: string,
  email?: string,
  tenantId?: string
): Promise<void> {
  await Promise.all([
    recordAuthEvent(
      { type: 'LoginSucceeded', timestamp: Date.now(), userId, email, tenantId },
      userId
    ),
    stampUserLogin(userId),
  ]);
}

export async function recordLoginFailure(
  email?: string,
  errorMessage?: string
): Promise<void> {
  await recordAuthEvent(
    { type: 'LoginFailed', timestamp: Date.now(), email, errorMessage },
    'anonymous'
  );
}

export async function recordLogout(userId: string): Promise<void> {
  await recordAuthEvent(
    { type: 'LogoutCompleted', timestamp: Date.now(), userId },
    userId
  );
}

export async function recordPermissionDenied(
  userId: string,
  action: string,
  resource?: string
): Promise<void> {
  await recordAuthEvent(
    {
      type: 'PermissionDenied',
      timestamp: Date.now(),
      userId,
      metadata: { action, resource },
    },
    userId
  );
}

/**
 * Stamps last_login_at and last_seen_at on the user's public.users row via
 * the record_user_login RPC (SECURITY DEFINER — bypasses RLS safely).
 */
async function stampUserLogin(userId: string): Promise<void> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.rpc('record_user_login', { user_uuid: userId });
    if (error && import.meta.env.DEV) {
      console.warn('[AuthTelemetry] Failed to stamp login timestamp:', error);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[AuthTelemetry] Error stamping login timestamp:', err);
    }
  }
}

export async function getAuthEventsForUser(
  userId: string,
  limit = 50
): Promise<AuthEventPayload[]> {
  try {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('audit_events')
      .select('payload')
      .eq('created_by', userId)
      .in('event_type', ['LoginSucceeded', 'LoginFailed', 'LogoutCompleted', 'PermissionDenied'])
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      if (import.meta.env.DEV) {
        console.warn('[AuthTelemetry] Failed to query events:', error);
      }
      return [];
    }

    return (data || []).map(row => row.payload as AuthEventPayload);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[AuthTelemetry] Error querying events:', err);
    }
    return [];
  }
}
